import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Alert,
  Stack,
  Box,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useLocation } from "react-router-dom";
import { AccountApi, Configuration } from "account-common";
import { useTranslation } from "i18n-fe";
import { useApiConfig } from "common-fe";
import { colors } from "brand";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

// Routes where the user is mid-auth-handoff or signing out: never gate there.
const UNGATED_PATHS = ["/callback", "/logout", "/logged-out"];

/**
 * NameGate: required first + last name modal (#476, folds in #286).
 *
 * One non-dismissable modal covering BOTH cases, so nobody is double-prompted:
 *   1. Post-registration gate: a brand-new signup has no given/family name.
 *   2. Login backfill (#286): an account that predates the name requirement
 *      logs in with a missing given_name/family_name.
 *
 * Whether a name is missing is decided from the ACCOUNT RECORD (GET /account,
 * Cognito GetUser), never from the ID token claims. #788 pulled the first
 * version of this gate because the token is stale right after a name is saved
 * (the modal could show for a user who already had a name). The record is the
 * source of truth; the claims only lag it. After a successful save the token
 * is refreshed best-effort so the nav name catches up.
 *
 * ACCOUNT APP ONLY. Lives in account-fe and is NOT exported from common-fe, so
 * the locked-down kiosk apps (scribe-fe, announcer-fe) can never render it: a
 * forced modal there would be an escape hatch out of the kiosk flow. Pinned by
 * src/components/NameGate.kiosk.test.js.
 *
 * If the account record cannot be read, the gate stays closed (fail open): an
 * API hiccup must not lock a signed-in user out of the app.
 */
export default function NameGate() {
  const auth = useAuth();
  const buildConfig = useApiConfig();
  const { t } = useTranslation("common");
  const { pathname } = useLocation();

  const accessToken = auth.user?.access_token;
  const signedIn = Boolean(auth.isAuthenticated) && Boolean(accessToken);

  const api = useMemo(() => {
    if (!accessToken) return null;
    return new AccountApi(new Configuration(buildConfig(ACCOUNT_API_BASE_URL)));
  }, [accessToken, buildConfig]);

  // null until the account record has been read; then { first, last }.
  const [record, setRecord] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!signedIn || !api || done) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAccountInfo();
        if (cancelled) return;
        const data = res.data?.status === "success" ? res.data.data : null;
        if (!data) return;
        const first = (data.firstName || "").trim();
        const last = (data.lastName || "").trim();
        setRecord({ first, last });
        setFirstName(first);
        setLastName(last);
      } catch {
        // fail open, see the component doc
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-reads on a new access token (silent renew) are cheap and harmless;
    // `done` latches the gate shut once a name has been saved.
  }, [signedIn, api, done]);

  const nameMissing = Boolean(record) && (!record.first || !record.last);
  const open =
    signedIn && nameMissing && !done && !UNGATED_PATHS.includes(pathname);

  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const canSubmit = Boolean(trimmedFirst) && Boolean(trimmedLast) && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || !api) return;
    setSaving(true);
    setStatus({ type: "", message: "" });
    try {
      const response = await api.updateAccount({
        firstName: trimmedFirst,
        lastName: trimmedLast,
      });
      if (response.data.status === "success") {
        setDone(true);
        // Best effort: pull a fresh ID token so the nav bar shows the new
        // name without a manual refresh. A failure here must not reopen or
        // block anything, the record is already saved.
        try {
          await auth.signinSilent?.();
        } catch {
          // ignore
        }
      } else {
        setStatus({ type: "error", message: t("nameGate.submitError") });
      }
    } catch (err) {
      setStatus({
        type: "error",
        message: err?.message || t("nameGate.submitError"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      // Non-dismissable: no escape key, backdrop-click closes are swallowed.
      // The only way out is a valid first + last name.
      disableEscapeKeyDown
      onClose={() => {}}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { "data-testid": "name-gate-dialog" } }}
    >
      <DialogTitle>{t("nameGate.title")}</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("nameGate.description")}
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              label={t("nameGate.firstNameLabel")}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              required
              autoFocus
              disabled={saving}
              slotProps={{ htmlInput: { "data-testid": "name-gate-firstName" } }}
            />
            <TextField
              label={t("nameGate.lastNameLabel")}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              required
              disabled={saving}
              slotProps={{ htmlInput: { "data-testid": "name-gate-lastName" } }}
            />
            {status.type && (
              <Alert severity={status.type}>{status.message}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            data-testid="name-gate-submit"
            // Brand primary is darkGrey, near-invisible on a dark paper
            // background, so force the foxOrange accent for legibility.
            sx={{
              bgcolor: colors.foxOrange,
              color: colors.darkGrey,
              fontWeight: 600,
              "&:hover": { bgcolor: "#e0890f" },
            }}
          >
            {saving ? t("nameGate.saving") : t("nameGate.submitButton")}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
